// assets
import ApartmentOutlined from '@ant-design/icons/ApartmentOutlined';
import AppstoreOutlined from '@ant-design/icons/AppstoreOutlined';
import BankOutlined from '@ant-design/icons/BankOutlined';
import BellOutlined from '@ant-design/icons/BellOutlined';
import BookOutlined from '@ant-design/icons/BookOutlined';
import BulbOutlined from '@ant-design/icons/BulbOutlined';
import CalendarOutlined from '@ant-design/icons/CalendarOutlined';
import FileOutlined from '@ant-design/icons/FileOutlined';
import FullscreenOutlined from '@ant-design/icons/FullscreenOutlined';
import HeartOutlined from '@ant-design/icons/HeartOutlined';
import LineChartOutlined from '@ant-design/icons/LineChartOutlined';
import MessageOutlined from '@ant-design/icons/MessageOutlined';
import ProfileOutlined from '@ant-design/icons/ProfileOutlined';
import SafetyCertificateOutlined from '@ant-design/icons/SafetyCertificateOutlined';
import SettingOutlined from '@ant-design/icons/SettingOutlined';
import StarOutlined from '@ant-design/icons/StarOutlined';
import TableOutlined from '@ant-design/icons/TableOutlined';
import TeamOutlined from '@ant-design/icons/TeamOutlined';
import TranslationOutlined from '@ant-design/icons/TranslationOutlined';
import UserOutlined from '@ant-design/icons/UserOutlined';

// ==============================|| MENU ITEMS - FROM GRANTED PERMISSIONS ||============================== //

/**
 * Turn GET /catalogue/my-menu into the tree NavGroup renders.
 *
 * This is what makes Role Definitions real. Before it, the sidebar was chosen from
 * hand-written trees by role code, so a role created in the UI got the therapist's
 * menu no matter what it had been granted. Now the granted features ARE the menu.
 *
 * The catalogue stores an icon as a short string; the menu needs a component, so the
 * two are mapped here. An unknown or missing icon falls back to a neutral one rather
 * than rendering nothing — a menu item with no icon looks broken, and the catalogue is
 * editable by administrators who cannot be expected to know the icon set.
 */
const ICONS = {
  bell: BellOutlined,
  book: BookOutlined,
  building: BankOutlined,
  bulb: BulbOutlined,
  calendar: CalendarOutlined,
  chat: MessageOutlined,
  child: UserOutlined,
  clipboard: ProfileOutlined,
  doc: FileOutlined,
  fullscreen: FullscreenOutlined,
  gear: SettingOutlined,
  grid: AppstoreOutlined,
  heart: HeartOutlined,
  language: TranslationOutlined,
  layers: ApartmentOutlined,
  puzzle: AppstoreOutlined,
  shield: SafetyCertificateOutlined,
  star: StarOutlined,
  table: TableOutlined,
  trend: LineChartOutlined,
  users: TeamOutlined
};

// Exported so the catalogue icon can drive icons in the static persona / admin
// menus too, not just this grant-built one — see applyCatalogueIcons.
export const iconFor = (name) => ICONS[name] || FileOutlined;

// The single list of selectable catalogue icon names, sorted for a stable
// dropdown. Every icon picker (Features & Pages, Guided Tour, …) renders THIS
// list via iconFor, so adding one entry to ICONS above makes it available
// everywhere — no per-screen copy to keep in sync.
export const ICON_NAMES = Object.keys(ICONS).sort();

// Normalise a URL for matching: drop the query string and any trailing slash so
// `/admin/dashboard` and `/admin/dashboard/` compare equal.
const normUrl = (u) => (typeof u === 'string' ? u.split('?')[0].split('#')[0].replace(/\/+$/, '') : '');

/**
 * Build a lookup from the GET /catalogue/my-menu payload that answers "what icon
 * did Features and Components set for this menu item?".
 *
 * The catalogue keeps ONE icon per FEATURE. A persona tree, though, splits a
 * feature into several page items (Dashboard & Reports → Dashboard, Reports), and
 * each of those carries the PAGE code. So a page resolves to its parent FEATURE's
 * icon — that is the icon an administrator actually edited, and what makes the
 * change show up wherever the feature appears.
 *
 * Keyed by catalogue code (feature or page) and, as a fallback for the handful of
 * static items that carry a URL but no code, by normalised URL.
 *
 * @param {object|null} payload GET /catalogue/my-menu. Null → null (nothing to apply).
 */
export function buildCatalogueIconIndex(payload) {
  const byCode = {};
  const byUrl = {};

  const ingest = (sections) => {
    for (const section of sections || []) {
      for (const feat of section.items || []) {
        if (!feat.icon) continue;
        if (feat.code) byCode[feat.code] = feat.icon;
        if (feat.url) byUrl[normUrl(feat.url)] = feat.icon;
        // Pages inherit the feature icon (see the note above).
        for (const page of feat.children || []) {
          if (page.code) byCode[page.code] = feat.icon;
          // Skip parameterised templates — they are never a menu destination.
          if (page.url && !page.url.includes(':')) byUrl[normUrl(page.url)] = feat.icon;
        }
      }
    }
  };

  ingest(payload?.sections);
  ingest(payload?.granted_menu);

  if (!Object.keys(byCode).length && !Object.keys(byUrl).length) return null;
  return { byCode, byUrl };
}

/**
 * Override each menu item's icon with the one the catalogue defines for it, so the
 * icon set in Features and Components is the single source of truth across every
 * menu (persona trees, the admin-levels menu, and the grant-built menu alike).
 *
 * An item with no catalogue match keeps its existing icon — structural entries
 * (Sync Status, section headings) and anything the catalogue does not know about
 * are left untouched rather than blanked.
 *
 * @param {Array} items The built menu tree.
 * @param {object|null} index From buildCatalogueIconIndex. Null → items unchanged.
 */
export function applyCatalogueIcons(items, index) {
  if (!index) return items;

  const resolve = (node) => {
    if (node.catalogueCode && index.byCode[node.catalogueCode]) return index.byCode[node.catalogueCode];
    if (node.url && index.byUrl[normUrl(node.url)]) return index.byUrl[normUrl(node.url)];
    return null;
  };

  const walk = (nodes) =>
    (nodes || []).map((node) => {
      const name = resolve(node);
      const next = name ? { ...node, icon: iconFor(name) } : { ...node };
      if (next.children) next.children = walk(next.children);
      return next;
    });

  return walk(items);
}

/**
 * A route is only navigable if it has no unfilled parameters. The catalogue stores
 * page routes as templates (`/supervisor/child/:childId/iep`), which are reachable
 * from inside a screen but cannot be a menu destination on their own.
 */
const isNavigable = (url) => typeof url === 'string' && url.length > 0 && !url.includes(':');

/**
 * @param {object|null} payload GET /catalogue/my-menu. Null means not yet known.
 * @returns {Array|null} Menu groups, or null when there is nothing usable — the
 *   caller then falls back to the static per-role trees rather than showing an
 *   empty sidebar.
 */
export function buildMenuFromGrants(payload) {
  const sections = payload?.sections;
  if (!Array.isArray(sections) || !sections.length) return null;

  const groups = sections
    .map((section, si) => {
      const children = (section.items || [])
        .map((feat, fi) => {
          // Only sub-pages that are navigable in their own right become children.
          const kids = (feat.children || [])
            .filter((p) => isNavigable(p.url))
            .map((p, pi) => ({
              id: `perm-${feat.code}-${p.code || pi}`,
              title: p.title,
              type: 'item',
              url: p.url,
              breadcrumbs: false,
              // The page code, so this item can be deduped against the static
              // persona trees (which key on the same code) and gated by visibility.
              catalogueCode: p.code
            }));

          if (!isNavigable(feat.url) && !kids.length) return null;

          // A feature with navigable children becomes a collapse; on its own it is a
          // plain item. Children whose url equals the parent's are dropped, or the
          // menu shows the same destination twice.
          const distinct = kids.filter((k) => k.url !== feat.url);
          if (distinct.length) {
            return {
              id: `perm-${feat.code || fi}`,
              title: feat.title,
              type: 'collapse',
              icon: iconFor(feat.icon),
              breadcrumbs: false,
              catalogueCode: feat.code,
              children: distinct
            };
          }
          return {
            id: `perm-${feat.code || fi}`,
            title: feat.title,
            type: 'item',
            url: feat.url,
            icon: iconFor(feat.icon),
            breadcrumbs: false,
            catalogueCode: feat.code
          };
        })
        .filter(Boolean);

      return children.length ? { id: `perm-group-${si}`, title: section.name, type: 'group', children } : null;
    })
    .filter(Boolean);

  return groups.length ? groups : null;
}

/**
 * Every URL the signed-in user has been granted, parameterised routes included.
 *
 * RoleGuard uses this to decide whether a path is permitted, which is what lets a
 * newly created role reach its screens without every one of the ~90 route guards
 * being rewritten to name it.
 */
export function grantedUrls(payload) {
  const out = [];
  for (const section of payload?.sections || []) {
    for (const feat of section.items || []) {
      if (feat.url) out.push(feat.url);
      for (const page of feat.children || []) {
        if (page.url) out.push(page.url);
      }
    }
  }
  return out;
}

export default buildMenuFromGrants;
